/**
 * @module DialogDescription
 *
 * @see https://0.vuetifyjs.com/components/disclosure/dialog
 *
 * @remarks
 * Description component for dialogs. Provides supplementary description
 * for the dialog via aria-describedby. Should be used inside DialogContent.
 */

<script lang="ts">
  // Types
  import type { AtomProps } from '#v0/components/Atom'

  export interface DialogDescriptionProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
  }

  export interface DialogDescriptionSlotProps {
    /** Attributes to bind to the description element */
    attrs: {
      id: string
    }
  }
</script>

<script setup lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import { useDialogContext } from './DialogRoot.vue'

  // Utilities
  import { onBeforeUnmount, onMounted, toRef } from 'vue'

  defineOptions({ name: 'DialogDescription' })

  defineSlots<{
    default: (props: DialogDescriptionSlotProps) => any
  }>()

  const {
    as = 'p',
    namespace = 'v0:dialog',
    renderless,
  } = defineProps<DialogDescriptionProps>()

  const context = useDialogContext(namespace)

  onMounted(() => {
    context.hasDescription.value = true
  })

  onBeforeUnmount(() => {
    context.hasDescription.value = false
  })

  const slotProps = toRef((): DialogDescriptionSlotProps => ({
    attrs: {
      id: context.descriptionId,
    },
  }))
</script>

<template>
  <Atom
    :as
    :renderless
    v-bind="slotProps.attrs"
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
